import React, { useState, useMemo } from "react";
import { Slider } from "@/components/ui/slider";
import { Calculator, ArrowRight, ShieldCheck, PiggyBank, TrendingDown, Euro, Target } from "lucide-react";
import { CALENDLY_URL } from "@/lib/siteData";

const clamp = (v, min, max) => Math.min(max, Math.max(min, v));

export default function Simulator() {
  // "amount" mode = user enters amount → computes monthly payment (mensualité)
  // "payment" mode = user enters monthly payment (échéance) → computes max borrowable amount
  const [mode, setMode] = useState("amount");
  const [amount, setAmount] = useState(250000);
  const [payment, setPayment] = useState(1400);
  const [years, setYears] = useState(20);
  const [rate, setRate] = useState(3.4);
  // Borrower insurance rate (annual, on initial capital) — 0.36% avg bank, 0.14% delegation
  const [insRate, setInsRate] = useState(0.34);

  const result = useMemo(() => {
    const n = years * 12;
    const r = rate / 100 / 12;

    let computedAmount = amount;
    let computedPayment = payment;

    if (mode === "amount") {
      // Compute payment from amount
      computedPayment = r === 0 ? amount / n : (amount * r * Math.pow(1 + r, n)) / (Math.pow(1 + r, n) - 1);
    } else {
      // Compute max amount from payment
      computedAmount = r === 0 ? payment * n : (payment * (Math.pow(1 + r, n) - 1)) / (r * Math.pow(1 + r, n));
    }

    const totalCredit = computedPayment * n;
    const interest = totalCredit - computedAmount;

    // Insurance: monthly premium = capital × annual_rate / 12
    const insMonthly = (computedAmount * insRate) / 100 / 12;
    const insTotal = insMonthly * n;
    const totalMonthly = computedPayment + insMonthly;

    // Loi Lemoine potential savings (0.36% vs 0.14%)
    const bankInsuranceTotal = (computedAmount * 0.36 / 100) * years;
    const delegInsuranceTotal = (computedAmount * 0.14 / 100) * years;
    const insuranceSavings = Math.max(0, bankInsuranceTotal - delegInsuranceTotal);

    return {
      amount: Math.round(computedAmount),
      monthly: Math.round(computedPayment),
      insMonthly: Math.round(insMonthly),
      totalMonthly: Math.round(totalMonthly),
      totalCredit: Math.round(totalCredit),
      interest: Math.round(interest),
      insTotal: Math.round(insTotal),
      insuranceSavings: Math.round(insuranceSavings),
    };
  }, [mode, amount, payment, years, rate, insRate]);

  return (
    <section id="simulateur" data-testid="simulator-section" className="py-24 md:py-32 bg-md-bg">
      <div className="max-w-7xl mx-auto px-6 md:px-12 grid lg:grid-cols-12 gap-12 items-start">
        <div className="lg:col-span-5 lg:sticky lg:top-32">
          <div className="text-xs uppercase tracking-[0.25em] text-md-accent font-bold mb-4">Simulateur de crédit</div>
          <h2 className="font-serif text-4xl md:text-5xl text-md-primary font-medium leading-tight mb-6">
            Estimez votre mensualité <span className="italic">en 10 secondes.</span>
          </h2>
          <p className="text-md-muted text-lg leading-relaxed mb-8">
            Ajustez le montant ou la mensualité, la durée, le taux et l'assurance emprunteur pour
            visualiser instantanément votre futur emprunt. Pour un taux personnalisé adapté à votre
            dossier, parlons-en autour d'un café.
          </p>
          <a
            href={CALENDLY_URL}
            target="_blank"
            rel="noreferrer"
            data-testid="simulator-cta-rdv"
            className="inline-flex items-center gap-2 bg-md-primary hover:bg-md-primaryHover text-white px-7 py-4 rounded-full font-semibold shadow-lg transition-all hover:-translate-y-0.5"
          >
            Obtenir mon taux réel
            <ArrowRight className="w-4 h-4" />
          </a>
          <a href="/simulateur-epargne" data-testid="simulator-savings-link" className="block mt-5 text-sm font-semibold text-md-secondary hover:underline">
            Vous souhaitez épargner ? Essayez le simulateur d'épargne →
          </a>
        </div>

        <div className="lg:col-span-7">
          <div className="bg-white rounded-3xl p-8 md:p-10 shadow-xl border border-md-border">
            <div className="flex items-center gap-3 mb-8">
              <div className="w-11 h-11 rounded-2xl bg-md-secondary/15 grid place-items-center">
                <Calculator className="w-5 h-5 text-md-secondary" />
              </div>
              <div>
                <div className="text-xs uppercase tracking-widest text-md-accent">Simulation indicative</div>
                <div className="font-serif text-xl text-md-primary">Crédit immobilier</div>
              </div>
            </div>

            {/* Mode toggle */}
            <div className="grid grid-cols-2 gap-2 p-1 bg-md-bg rounded-full mb-8" data-testid="sim-mode-toggle">
              <ModeButton
                testId="sim-mode-amount"
                active={mode === "amount"}
                onClick={() => setMode("amount")}
                icon={Euro}
                label="Je connais mon montant"
              />
              <ModeButton
                testId="sim-mode-payment"
                active={mode === "payment"}
                onClick={() => setMode("payment")}
                icon={Target}
                label="Je connais ma mensualité"
              />
            </div>

            <div className="space-y-7">
              {mode === "amount" ? (
                <EditableSliderField
                  testId="sim-amount"
                  label="Montant emprunté"
                  value={amount}
                  unit="€"
                  min={20000}
                  max={1000000}
                  step={5000}
                  decimals={0}
                  onChange={(v) => setAmount(clamp(v, 20000, 1000000))}
                />
              ) : (
                <EditableSliderField
                  testId="sim-payment"
                  label="Mensualité souhaitée (crédit hors assurance)"
                  value={payment}
                  unit="€"
                  min={100}
                  max={10000}
                  step={50}
                  decimals={0}
                  onChange={(v) => setPayment(clamp(v, 100, 10000))}
                />
              )}
              <EditableSliderField
                testId="sim-years"
                label="Durée du prêt"
                value={years}
                unit="ans"
                min={5}
                max={30}
                step={1}
                decimals={0}
                onChange={(v) => setYears(clamp(v, 5, 30))}
              />
              <EditableSliderField
                testId="sim-rate"
                label="Taux d'intérêt annuel"
                value={rate}
                unit="%"
                min={1}
                max={7}
                step={0.05}
                decimals={2}
                onChange={(v) => setRate(clamp(parseFloat(v.toFixed(2)), 1, 7))}
              />
              <EditableSliderField
                testId="sim-insurance-rate"
                label="Taux d'assurance emprunteur annuel"
                hint="Moyenne : 0,36 % (banque) — 0,14 % (délégation)"
                value={insRate}
                unit="%"
                min={0.05}
                max={1}
                step={0.01}
                decimals={2}
                onChange={(v) => setInsRate(clamp(parseFloat(v.toFixed(2)), 0.05, 1))}
              />
            </div>

            {/* Results */}
            <div className="mt-10 pt-7 border-t border-md-border space-y-4">
              {mode === "payment" && (
                <ResultRow
                  testId="sim-result-max-amount"
                  label="Montant empruntable"
                  value={`${result.amount.toLocaleString("fr-FR")} €`}
                  highlight
                />
              )}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <ResultBlock
                  testId="sim-result-monthly"
                  label="Mensualité crédit"
                  value={`${result.monthly.toLocaleString("fr-FR")} €`}
                />
                <ResultBlock
                  testId="sim-result-insurance-monthly"
                  label="Assurance / mois"
                  value={`${result.insMonthly.toLocaleString("fr-FR")} €`}
                  accent
                />
                <ResultBlock
                  testId="sim-result-total-monthly"
                  label="Mensualité totale"
                  value={`${result.totalMonthly.toLocaleString("fr-FR")} €`}
                  highlight
                />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <ResultBlock testId="sim-result-total" label="Coût total crédit" value={`${result.totalCredit.toLocaleString("fr-FR")} €`} muted />
                <ResultBlock testId="sim-result-interest" label="Intérêts" value={`${result.interest.toLocaleString("fr-FR")} €`} muted />
                <ResultBlock testId="sim-result-insurance-total" label="Coût assurance" value={`${result.insTotal.toLocaleString("fr-FR")} €`} muted />
              </div>
            </div>

            {/* Loi Lemoine banner */}
            <div data-testid="sim-insurance-banner" className="mt-6 relative overflow-hidden rounded-2xl bg-gradient-to-br from-md-accent to-md-primary text-white p-6">
              <div className="absolute -top-10 -right-10 w-40 h-40 bg-md-secondary/30 rounded-full blur-3xl" />
              <div className="relative flex flex-col sm:flex-row sm:items-center gap-5">
                <div className="w-14 h-14 rounded-2xl bg-white/15 grid place-items-center flex-shrink-0">
                  <PiggyBank className="w-7 h-7 text-md-secondary" />
                </div>
                <div className="flex-1">
                  <div className="text-[10px] uppercase tracking-[0.2em] text-md-secondary font-bold mb-1 flex items-center gap-2">
                    <TrendingDown className="w-3 h-3" /> Loi Lemoine — Économie estimée
                  </div>
                  <div className="font-serif text-2xl md:text-3xl leading-tight">
                    Jusqu'à <span className="text-md-secondary font-semibold">{result.insuranceSavings.toLocaleString("fr-FR")} €</span> d'économies
                  </div>
                  <p className="text-sm text-white/80 mt-2 leading-relaxed">
                    En déléguant votre <strong className="text-white">assurance emprunteur</strong> à un assureur externe (vs. assurance groupe bancaire),
                    vous pouvez économiser plusieurs milliers d'euros sur la durée du prêt — à garanties équivalentes.
                  </p>
                </div>
                <a
                  href={CALENDLY_URL}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="sim-insurance-cta"
                  className="inline-flex items-center gap-2 bg-md-secondary hover:bg-md-secondaryHover text-white px-5 py-3 rounded-full text-sm font-semibold transition-all flex-shrink-0 self-start sm:self-center"
                >
                  <ShieldCheck className="w-4 h-4" /> Comparer
                </a>
              </div>
            </div>

            <p className="text-xs text-md-muted mt-6 leading-relaxed">
              * Simulation indicative hors frais de dossier et de garantie. Estimation d'assurance sur la base d'un taux fixe
              appliqué au capital initial (méthode courante). Le taux d'assurance réel dépend de votre âge, votre profil et
              vos garanties. Ce calcul ne constitue pas une offre de prêt. Un crédit vous engage et doit être remboursé.
              Vérifiez vos capacités de remboursement avant de vous engager.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

function ModeButton({ active, onClick, icon: Icon, label, testId }) {
  return (
    <button
      type="button"
      data-testid={testId}
      onClick={onClick}
      className={`inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-full text-sm font-medium transition-all ${
        active
          ? "bg-md-primary text-white shadow-md"
          : "text-md-muted hover:text-md-primary"
      }`}
    >
      <Icon className="w-4 h-4" />
      <span className="hidden sm:inline">{label}</span>
      <span className="sm:hidden">{label.split(" ").slice(-2).join(" ")}</span>
    </button>
  );
}

export function EditableSliderField({ label, value, unit, min, max, step, decimals, onChange, testId, hint }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");

  const formatted = decimals > 0
    ? value.toLocaleString("fr-FR", { minimumFractionDigits: decimals, maximumFractionDigits: decimals })
    : value.toLocaleString("fr-FR");

  const commit = () => {
    const cleaned = draft.replace(/\s/g, "").replace(",", ".");
    const num = parseFloat(cleaned);
    if (!isNaN(num)) onChange(num);
    setEditing(false);
    setDraft("");
  };

  return (
    <div data-testid={testId}>
      <div className="flex justify-between items-baseline mb-3 gap-3">
        <label className="text-sm text-md-muted">
          {label}
          {hint && <span className="block text-[10px] text-md-muted/70 mt-0.5">{hint}</span>}
        </label>
        {editing ? (
          <div className="flex items-center gap-2">
            <input
              data-testid={`${testId}-input`}
              autoFocus
              type="text"
              inputMode="decimal"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onBlur={commit}
              onKeyDown={(e) => { if (e.key === "Enter") commit(); if (e.key === "Escape") { setEditing(false); setDraft(""); } }}
              className="w-32 text-right font-serif text-2xl text-md-primary font-medium bg-md-bg border border-md-secondary rounded-lg px-2 py-1 focus:outline-none"
            />
            <span className="font-serif text-2xl text-md-primary">{unit}</span>
          </div>
        ) : (
          <button
            type="button"
            data-testid={`${testId}-display`}
            onClick={() => { setDraft(String(value).replace(".", ",")); setEditing(true); }}
            className="group inline-flex items-center gap-1.5 font-serif text-2xl text-md-primary font-medium bg-md-bg border-2 border-dashed border-md-secondary/50 hover:border-md-secondary hover:bg-md-secondary/10 rounded-lg px-3 py-1 transition-all"
            title="Cliquez pour saisir une valeur"
          >
            <svg className="w-3.5 h-3.5 text-md-secondary opacity-70 group-hover:opacity-100" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 20h9" />
              <path d="M16.5 3.5a2.121 2.121 0 013 3L7 19l-4 1 1-4L16.5 3.5z" />
            </svg>
            <span>{formatted} {unit}</span>
          </button>
        )}
      </div>
      <Slider
        value={[value]}
        min={min}
        max={max}
        step={step}
        onValueChange={(v) => onChange(v[0])}
        className="[&_[role=slider]]:bg-md-secondary [&_[role=slider]]:border-md-secondary"
      />
      <div className="flex justify-between text-[10px] uppercase tracking-widest text-md-muted/60 mt-1.5">
        <span>{decimals > 0 ? min.toFixed(decimals) : min.toLocaleString("fr-FR")} {unit}</span>
        <span>{decimals > 0 ? max.toFixed(decimals) : max.toLocaleString("fr-FR")} {unit}</span>
      </div>
    </div>
  );
}

function ResultBlock({ label, value, testId, highlight, accent, muted }) {
  const cls = highlight
    ? "bg-md-primary text-white"
    : accent
    ? "bg-md-secondary/15 text-md-primary border border-md-secondary/30"
    : muted
    ? "bg-md-bg text-md-primary/80"
    : "bg-md-bg text-md-primary";
  const labelCls = highlight ? "text-md-secondary" : accent ? "text-md-secondary" : "text-md-muted";
  const valueCls = highlight ? "text-white" : "text-md-primary";
  return (
    <div data-testid={testId} className={`p-4 rounded-2xl ${cls}`}>
      <div className={`text-[10px] uppercase tracking-widest mb-2 font-bold ${labelCls}`}>{label}</div>
      <div className={`font-serif text-xl md:text-2xl font-medium ${valueCls}`}>{value}</div>
    </div>
  );
}

function ResultRow({ label, value, testId, highlight }) {
  return (
    <div
      data-testid={testId}
      className={`flex items-center justify-between px-5 py-4 rounded-2xl ${
        highlight ? "bg-md-primary text-white" : "bg-md-bg text-md-primary"
      }`}
    >
      <div className={`text-[11px] uppercase tracking-widest font-bold ${highlight ? "text-md-secondary" : "text-md-accent"}`}>
        {label}
      </div>
      <div className={`font-serif text-2xl md:text-3xl font-medium ${highlight ? "text-white" : "text-md-primary"}`}>
        {value}
      </div>
    </div>
  );
}
